import React, { useState } from 'react';
import {
    Avatar,
    Button,
    CssBaseline,
    TextField,
    FormControlLabel,
    Checkbox,
    Paper,
    Box,
    Grid,
    Typography,
    InputAdornment,
    IconButton,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import { initSession } from '../services/authService';

export default function Login({ onLogin }) {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);

    const performLogin = async (user, pass) => {
        setErrorMessage('');
        setLoading(true);
        try {
            let username = (user || '').trim();
            if (username.includes('@')) {
                const prefix = username.split('@')[0].toLowerCase();
                if (['superadmin', 'admin', 'manager', 'cashier'].includes(prefix)) {
                    username = prefix;
                }
            }

            // Primary endpoint with automatic 127.0.0.1 fallback for Windows WSL2 IPv6 resets
            const endpoints = ['/api/auth/login'];
            if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
                endpoints.push(`http://127.0.0.1:${window.location.port || '3000'}/api/auth/login`);
            }

            let response = null;
            let lastError = null;

            for (const endpoint of endpoints) {
                try {
                    response = await fetch(endpoint, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ username, password: pass })
                    });
                    if (response) break;
                } catch (netErr) {
                    lastError = netErr;
                }
            }

            if (!response) {
                throw lastError || new Error('Network error');
            }

            if (response.ok) {
                const data = await response.json();
                initSession(data.token, data.role, username);
                onLogin(data.token);
                navigate('/');
            } else {
                const msg = 'Invalid username or password. Please try again.';
                setErrorMessage(msg);
                alert(msg);
            }
        } catch (error) {
            console.error('Login error', error);
            const msg = 'Failed to connect to backend. Tip: If using Windows WSL2, access via http://127.0.0.1:3000';
            setErrorMessage(msg);
            alert('Failed to connect to backend.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (event) => {
        if (event && event.preventDefault) event.preventDefault();
        await performLogin(email, password);
    };

    return (
        <Grid container component="main" sx={{ height: '100vh' }}>
            <CssBaseline />
            <Grid
                item
                xs={false}
                sm={4}
                md={7}
                sx={{
                    backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundRepeat: 'no-repeat',
                    backgroundColor: (t) =>
                        t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.grey[900],
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: 'white',
                    p: 4
                }}
            >
                <Typography variant="h2" fontWeight="bold">SIMS</Typography>
                <Typography variant="h6" sx={{ mt: 1, opacity: 0.9 }}>
                    Smart Inventory & Omnichannel POS System
                </Typography>
            </Grid>
            <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
                <Box
                    sx={{
                        my: { xs: 4, sm: 8 },
                        mx: { xs: 2, sm: 4 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
                        <LockOutlinedIcon />
                    </Avatar>
                    <Typography component="h1" variant="h5">
                        Sign in
                    </Typography>

                    {errorMessage && (
                        <Box sx={{ mt: 2, width: '100%', p: 1.5, bgcolor: '#fee2e2', color: '#991b1b', borderRadius: 1, border: '1px solid #f87171' }}>
                            <Typography variant="body2">{errorMessage}</Typography>
                        </Box>
                    )}

                    <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 2, width: '100%' }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address / Username"
                            name="email"
                            autoComplete="username"
                            autoFocus
                            value={email}
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) => setEmail(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px',
                                },
                                '& input:-webkit-autofill': {
                                    WebkitBoxShadow: '0 0 0 1000px #141414 inset !important',
                                    WebkitTextFillColor: '#ffffff !important',
                                    caretColor: '#ffffff !important',
                                    borderRadius: 'inherit !important',
                                },
                            }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            autoComplete="current-password"
                            value={password}
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) => setPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle visibility"
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            size="small"
                                            sx={{ color: 'text.secondary' }}
                                        >
                                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px',
                                },
                                '& input:-webkit-autofill': {
                                    WebkitBoxShadow: '0 0 0 1000px #141414 inset !important',
                                    WebkitTextFillColor: '#ffffff !important',
                                    caretColor: '#ffffff !important',
                                    borderRadius: 'inherit !important',
                                },
                            }}
                        />
                        <FormControlLabel
                            control={<Checkbox value="remember" color="primary" />}
                            label="Remember me"
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            sx={{ mt: 3, mb: 2 }}
                        >
                            {loading ? 'Signing In...' : 'Sign In'}
                        </Button>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Need access? Ask your SIMS administrator to create your account.
                        </Typography>
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
}
